import React from 'react';
import { Clock, CheckCircle2, Scissors } from 'lucide-react';
import { formatShortDay } from '../../lib/overOrder';

/**
 * What an over-ordering hold looks like to the client who placed it: what is
 * being held, by when the bakery will have decided, and — once decided — which
 * lines moved and to what.
 *
 * Renders nothing for an order that merely carried a warning: a client in good
 * standing is told at checkout and then left alone.
 */
export const OverCapNotice = ({ order, compact = false }) => {
  const hold = order?.over_cap;
  if (!hold) return null;
  const isHeld = order.status === 'pending_approval';
  const resolution = hold.resolution;
  if (!isHeld && !resolution) return null;

  const changes = resolution?.changes || [];
  const trimmed = resolution && changes.length > 0;

  const tone = isHeld
    ? 'border-amber-200 bg-amber-50 text-amber-900'
    : trimmed
      ? 'border-slate-200 bg-slate-50 text-slate-800'
      : 'border-green-200 bg-green-50 text-green-900';

  const Icon = isHeld ? Clock : trimmed ? Scissors : CheckCircle2;
  const deadline = formatShortDay(hold.approval_deadline);

  const heading = isHeld
    ? 'Waiting for our approval'
    : trimmed
      ? 'Approved with reductions'
      : 'Approved as ordered';

  return (
    <div
      className={`rounded-lg border px-3 py-2 ${tone}`}
      data-testid={`over-cap-notice-${order.id || order.order_number}`}
      data-held={isHeld ? 'true' : 'false'}
    >
      <div className="flex items-start gap-2">
        <Icon className="h-4 w-4 shrink-0 mt-0.5" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold">{heading}</p>
          {isHeld ? (
            <p className="text-xs mt-0.5 opacity-90">
              {hold.lines?.length || 0} line{(hold.lines?.length || 0) === 1 ? '' : 's'} above your best day.
              {deadline ? ` We confirm by ${deadline}.` : ' We confirm before the ordering deadline.'}
              {' '}Nothing goes to the kitchen until we do.
            </p>
          ) : trimmed ? (
            <p className="text-xs mt-0.5 opacity-90">
              {changes.map((c) => `${c.product_name} ${c.from_qty} → ${c.to_qty}`).join(' · ')}
            </p>
          ) : (
            <p className="text-xs mt-0.5 opacity-90">
              We kept the quantities you asked for.
            </p>
          )}

          {!compact && isHeld && hold.lines?.length > 0 && (
            <ul className="mt-2 space-y-1 max-w-2xl">
              {hold.lines.map((line) => (
                <li
                  key={line.product_id}
                  className="flex flex-wrap items-center justify-between gap-x-3 gap-y-0.5 text-xs"
                  data-testid={`over-cap-notice-line-${line.product_id}`}
                >
                  <span className="truncate">{line.product_name}</span>
                  <span className="shrink-0 tabular-nums">
                    {line.ordered_qty} ordered · cap {line.cap_qty} · best day {line.peak_sold}
                  </span>
                </li>
              ))}
            </ul>
          )}

          {!compact && hold.reason_label && (
            <p className="text-xs mt-2 opacity-75">
              Your reason: {hold.reason_label}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default OverCapNotice;
